import {
  GalleryModal,
  ImageGallery,
  LocationCard,
  PropertyDescription,
  PropertyFactsAndFeatures,
  PropertyFeatures,
  SellerInformationCard,
} from '@/components/properties/details';
import PropertyDisplay from '@/components/properties/PropertyDisplay';
import { MotionButton } from '@/components/ui/motion-button';
import { usePropertyQueries } from '@/hooks/queries/usePropertyQueries';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/hooks/useToast';
import { staggerContainer, staggerItem, viewportOptions } from '@/lib/animations';
import { formatAddress, formatPrice } from '@/lib/utils';
import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { ChevronLeft } from 'lucide-react';
import { useEffect, useState } from 'react';

export const Route = createFileRoute('/_public/properties/$id')({
  component: PropertyDetailPage,
})

export default function PropertyDetailPage() {
  const { id } = useParams({ from: '/_public/properties/$id' });
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [activeGalleryImage, setActiveGalleryImage] = useState(0);
  const { useFetchProperty, deletePropertyMutation } = usePropertyQueries();
  const { data: property } = useFetchProperty(id);
  const { profile } = useAuth();

  // Set document title
  useEffect(() => {
    document.title = `${property.list_price != null ? `${formatPrice(property.list_price)} | ` : ''}${formatAddress(property)} | Swim Homes`;
  }, [property]);

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast({
      title: 'Link copied!',
      description: 'The link to this property has been copied to your clipboard.'
    });
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this listing? This action cannot be undone.')) return;
    await deletePropertyMutation.mutateAsync({ propertyId: id },
      {
        onSuccess: () => {
          toast({
            title: 'Listing deleted',
            description: 'The property listing has been deleted.',
          });
        },
        onError: () => {
          toast({
            title: 'Error deleting listing',
            description: 'There was a problem deleting the listing.',
            variant: 'destructive',
          });
        },
      }
    );
  };

  return (
    <>
      {/* Gallery Modal */}
      <GalleryModal
        property={property}
        galleryOpen={galleryOpen}
        setGalleryOpen={setGalleryOpen}
        activeGalleryImage={activeGalleryImage}
        setActiveGalleryImage={setActiveGalleryImage}
      />

      {/* Main content with proper spacing */}
      <motion.div 
        className="max-w-6xl mx-auto space-y-6 py-6 px-4 relative z-10"
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={viewportOptions}
      >
        {/* Navigation and actions bar */}
        <motion.div 
          variants={staggerItem}
          className="flex items-center justify-between w-full"
        >
          <Link to="/properties">
            <MotionButton variant="ghost" className="flex items-center gap-1">
              <ChevronLeft className="h-4 w-4" />
              Back to Listings
            </MotionButton>
          </Link>
          <div className="flex items-center gap-2">
            <MotionButton
              variant="outline"
              size="sm"
              onClick={handleShare}
            >
              Share
            </MotionButton>
            {profile?.swimhomes_admin && (
              <MotionButton
                variant="destructive"
                size="sm"
                onClick={handleDelete}
                disabled={deletePropertyMutation.isPending}
              >
                {deletePropertyMutation.isPending ? 'Deleting...' : 'Delete Listing'}
              </MotionButton>
            )}
          </div>
        </motion.div>

        {/* Property header with main details */}
        <motion.div variants={staggerItem}>
          <PropertyDisplay
            property={property}
            variant="header"
            showButton={false}
          />
        </motion.div>

        {/* Image gallery - always shown */}
        <motion.div variants={staggerItem}>
          <ImageGallery
            property={property}
            galleryOpen={galleryOpen}
            setGalleryOpen={setGalleryOpen}
            activeGalleryImage={activeGalleryImage}
            setActiveGalleryImage={setActiveGalleryImage}
          />
        </motion.div>

        {/* Two-column layout for details */}
        <motion.div 
          variants={staggerItem}
          className="grid grid-cols-1 lg:grid-cols-3 gap-6"
        >
          {/* Main content column */}
          <div className="lg:col-span-2 space-y-6">
            <PropertyDescription property={property} />
            <PropertyFeatures property={property} />
            <PropertyFactsAndFeatures property={property} />
          </div>

          {/* Sidebar column */}
          <div className="space-y-6">
            <SellerInformationCard property={property} />
            <LocationCard property={property} />
          </div>
        </motion.div>
      </motion.div>
    </>
  );
}